import React, { useState } from "react";

import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  List,
  Row,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTrackingCode } from "react-hubspot-tracking-code-hook";

import Password from "antd/es/input/Password";
import {
  CheckOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { passwordValidatorPattern } from "../../utils/validators";
import { useResponsive } from "../../utils/use-responsive";
import { FormPage } from "../contents/form-page";
import { useUser } from '../../context/user.context';

const { Title } = Typography;

export const CreateAccountPage: React.FC = () => {
  const { setPathPageView } = useTrackingCode();
  setPathPageView("/create-account");

  const navigate = useNavigate();
  const { signUp } = useUser();
  const { isTablet } = useResponsive();

  const [searchParams] = useSearchParams();
  const [isCountCorrect, setIsCountCorrect] = useState(false);
  const [isUppercase, setIsUppercase] = useState(false);
  const [isLowercase, setIsLowercase] = useState(false);
  const [isNumber, setIsNumber] = useState(false);
  const [isSpecial, setIsSpecial] = useState(false);
  const [focus, setFocus] = useState(false);
  const [form] = Form.useForm();

  const data = [
    "Minimum length 8 characters",
    "Contains at least 1 number",
    "Contains at least 1 lowercase letter",
    "Contains at least 1 uppercase letter",
    "Contains at least 1 special character",
  ];

  function isCorrect(index: number) {
    return (
      (index === 0 && isCountCorrect) ||
      (index === 1 && isNumber) ||
      (index === 2 && isLowercase) ||
      (index === 3 && isUppercase) ||
      (index === 4 && isSpecial)
    );
  }

  const text = (
    <div style={{ padding: "20px", width: "100%" }}>
      <Space direction={"vertical"} size={"small"}>
        <Title level={5}>Requirements</Title>
        <List
          dataSource={data}
          renderItem={(item, index) => (
            <List.Item>
              {isCorrect(index) ? (
                <CheckOutlined style={{ color: "green" }} />
              ) : (
                <CloseOutlined style={{ color: "red" }} />
              )}
              &nbsp;
              {item}
            </List.Item>
          )}
        />
      </Space>
    </div>
  );

  const onFinish = async (values: any) => {
    values.token = searchParams.get("token");
    values.userId = searchParams.get("userId");
    signUp
      .execute(values)
      .then(() => navigate("/login"))
      .catch((error: any) => {
        //
      });
  };

  function refreshChecklist(event: { target: { value: any } }) {
    const value = event.target.value;

    const regExCount = /^.{8,256}$/;
    setIsCountCorrect(regExCount.test(value));

    const regExUppercase = /[A-Z]+/;
    setIsUppercase(regExUppercase.test(value));

    const regExLowercase = /[a-z]+/;
    setIsLowercase(regExLowercase.test(value));

    const regExNumber = /[0-9]+/;
    setIsNumber(regExNumber.test(value));

    const regExSpecial = /[|^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]+/;
    setIsSpecial(regExSpecial.test(value));
  }

  function passwordFormItem() {
    return (
      <Form.Item
        name="password"
        label="Password"
        rules={[
          { required: true, message: "Please input your Password!" },
          {
            pattern: passwordValidatorPattern,
            message: "",
          },
          {
            max: 24,
            message: "Value should be less than 24 character",
          },
        ]}
      >
        <Password
          size="large"
          type="password"
          placeholder="Password"
          data-cy="signup-password-input"
          onChange={refreshChecklist}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
        />
      </Form.Item>
    );
  }

  return (
    <FormPage displayFooter={true}>
        <Spin
          size={"large"}
          tip="Loading..."
          className={"full-width"}
          spinning={signUp?.loading}
        >
          <Space
            direction={"vertical"}
            size={"small"}
            className={"full-width"}
            style={{ width: "350px" }}
          >
            <Space direction={"vertical"} size={"small"}>
              <Title style={{ marginLeft: '25px' }} level={2}>
                Sign Up
              </Title>
            </Space>
            <Card>
              {signUp?.error && (
                <Alert
                  className={"alert"}
                  message="Please contact customer support for further assistance"
                  data-cy="signup-error-message-text"
                  type="error"
                />
              )}
              <Form
                name="normal_login"
                layout={"vertical"}
                requiredMark={false}
                className="login-form"
                onFinish={onFinish}
                form={form}
              >
                <Form.Item
                  name="email"
                  label="Email"
                  initialValue={searchParams.get("email")}
                >
                  <Input
                    size="large"
                    type="email"
                    placeholder="Email"
                    data-cy="signup-email-input"
                    disabled={true}
                  />
                </Form.Item>
                {isTablet ? (
                  passwordFormItem()
                ) : (
                  <Tooltip
                    id="tooltip-password"
                    placement="right"
                    title={text}
                    color={"white"}
                    open={focus}
                    overlayStyle={{ width: "100%" }}
                    overlayInnerStyle={{ width: "300px" }}
                  >
                    {passwordFormItem()}
                  </Tooltip>
                )}

                <Form.Item
                  name="confirmPassword"
                  label="Confirm Password"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your Password!",
                    },
                    {
                      max: 24,
                      message: "Value should be less than 24 character",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error(
                            "The two passwords that you entered do not match!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Password
                    size="large"
                    type="password"
                    placeholder="Password"
                    data-cy="signup-confirm-password-input"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Space direction={"vertical"} className={"full-width"}>
                  {/*  <Text>
                By using this service you are agreeing to our{' '}
                <a
                  href="https://financefair.com/terms-and-conditions/"
                  style={{ fontWeight: 500 }}
                  target={'_blank'}
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://financefair.com/privacy-policy/"
                  style={{ fontWeight: 500 }}
                  target={'_blank'}
                >
                  Privacy Policy
                </a>
                .
              </Text>*/}

                  <Button block type="primary" htmlType="submit">
                    Sign Up
                  </Button>
                </Space>
              </Form>
            </Card>
          </Space>
        </Spin>
    </FormPage>
  );
};
